import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ViewerItem } from '../media-viewer/media-viewer.component';

/** A clip in the grid and the place it holds. */
interface Cell { clip: ViewerItem; place: number; }

/**
 * The clips selected in the gallery, playing together in a grid (up to 4). A click on one opens it
 * alone in the viewer, above the grid; while the viewer covers the grid, the grid pauses and leaves
 * the keyboard to it. The strip below adds and takes out clips without closing the grid, and a clip
 * dragged onto another place moves there.
 *
 * The gallery owns the selection: `places` is the grid place by place (null = an empty place), and
 * every change goes back to it as toggle / move, so its ticks match the grid when the grid closes.
 */
@Component({
  selector: 'app-clip-grid',
  templateUrl: './clip-grid.component.html',
  styleUrls: ['./clip-grid.component.scss'],
  standalone: false
})
export class ClipGridComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() places: ViewerItem[] = [];
  /** Every clip of the exam, for the strip. */
  @Input() all: ViewerItem[] = [];
  @Input() max = 4;
  /** The viewer is open on top of the grid. */
  @Input() covered = false;
  @Output() closed = new EventEmitter<void>();
  @Output() open = new EventEmitter<ViewerItem>();
  @Output() toggle = new EventEmitter<ViewerItem>();
  @Output() move = new EventEmitter<{ from: number; to: number }>();
  /** The ? button: the gallery opens its shortcuts. */
  @Output() help = new EventEmitter<void>();
  @ViewChild('root') root: ElementRef<HTMLElement>;
  @ViewChild('grid') grid: ElementRef<HTMLElement>;
  @ViewChildren('vid') vids: QueryList<ElementRef<HTMLVideoElement>>;

  /**
   * The clips in a fixed order (by address), each placed on the grid by CSS. Moving a clip changes
   * only its place, never its spot in the page, so no video restarts when the grid is rearranged.
   */
  cells: Cell[] = [];
  /** The empty places, which a clip can be dragged onto. */
  holes: number[] = [];
  /** Clips paused one by one, or all at once with Pause all (by src). */
  paused = new Set<string>();
  /** Clips whose first frame has arrived; the others show the loader (by src). */
  ready = new Set<string>();
  full = false;
  limitHit = false;
  private limitTimer: any;

  drag: { src: string; from: number; x: number; y: number; dx: number; dy: number; moved: boolean; id: number; el: HTMLElement } = null;
  /** The place a dragged clip would land on. */
  over: number = null;
  private dragJustEnded = false;

  get count(): number { return this.cells.length; }
  /** One clip fills the grid, two sit side by side, three or four share the 2x2 places. */
  get placeCount(): number { return this.count <= 2 ? this.count : 4; }
  get cols(): number { return this.placeCount <= 1 ? 1 : 2; }
  get rows(): number { return this.placeCount === 4 ? 2 : 1; }
  row(place: number): number { return Math.floor(place / this.cols) + 1; }
  col(place: number): number { return (place % this.cols) + 1; }

  inGrid(c: ViewerItem): boolean { return this.cells.some((x) => x.clip.src === c.src); }
  trackCell(_: number, x: Cell): string { return x.clip.src; }

  ngOnChanges(c: SimpleChanges): void {
    if (c['places']) {
      const cells: Cell[] = [];
      this.places.forEach((clip, place) => { if (clip) cells.push({ clip, place }); });
      this.cells = cells.sort((a, b) => (a.clip.src < b.clip.src ? -1 : a.clip.src > b.clip.src ? 1 : 0));
      this.holes = [];
      for (let p = 0; p < this.placeCount; p++) if (!this.places[p]) this.holes.push(p);
    }
    if (c['covered'] && this.vids) this.apply();
  }
  ngAfterViewInit(): void { this.apply(); this.vids.changes.subscribe(() => this.apply()); }

  get allPaused(): boolean { return this.count > 0 && this.cells.every((x) => this.paused.has(x.clip.src)); }

  togglePlay(): void {
    if (this.allPaused) this.paused.clear();
    else this.cells.forEach((x) => this.paused.add(x.clip.src));
    this.apply();
  }
  toggleOne(c: ViewerItem, e: Event): void {
    e.stopPropagation();
    if (this.paused.has(c.src)) this.paused.delete(c.src); else this.paused.add(c.src);
    this.apply();
  }
  // While the viewer covers the grid, every clip waits.
  private apply(): void {
    if (!this.vids) return;
    this.vids.forEach((v) => {
      const el = v.nativeElement;
      if (!this.covered && !this.paused.has(el.dataset['src'])) el.play().catch(() => {}); else el.pause();
    });
  }

  /** From the strip: in or out of the grid. A fifth clip is refused here, as in the gallery. */
  pick(c: ViewerItem): void {
    if (!this.inGrid(c) && this.count >= this.max) {
      this.limitHit = true;
      clearTimeout(this.limitTimer);
      this.limitTimer = setTimeout(() => (this.limitHit = false), 2500);
      return;
    }
    this.toggle.emit(c);
  }
  takeOut(c: ViewerItem, e: Event): void {
    e.stopPropagation();
    this.toggle.emit(c);
  }

  // ---------------------------------------------------------------- moving a clip to another place
  // A press that moves more than a few pixels lifts the clip; released over another place it moves
  // there (onto a clip, the two swap). A press that does not move is an ordinary click.
  press(e: PointerEvent, x: Cell, el: HTMLElement): void {
    if (e.button !== 0 || this.placeCount < 2) return;
    this.drag = { src: x.clip.src, from: x.place, x: e.clientX, y: e.clientY, dx: 0, dy: 0, moved: false, id: e.pointerId, el };
  }
  drift(e: PointerEvent): void {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    d.dx = e.clientX - d.x;
    d.dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(d.dx, d.dy) < 8) return;
    if (!d.moved) { d.moved = true; d.el.setPointerCapture(e.pointerId); }
    const r = this.grid.nativeElement.getBoundingClientRect();
    const col = Math.floor(((e.clientX - r.left) / r.width) * this.cols);
    const row = Math.floor(((e.clientY - r.top) / r.height) * this.rows);
    const place = row * this.cols + col;
    this.over = col >= 0 && col < this.cols && row >= 0 && row < this.rows && place !== d.from ? place : null;
  }
  drop(e: PointerEvent, cancelled = false): void {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    if (d.moved) {
      if (!cancelled && this.over !== null) this.move.emit({ from: d.from, to: this.over });
      // the click that follows a drag must not open the clip it ended on
      this.dragJustEnded = true;
      setTimeout(() => (this.dragJustEnded = false));
    }
    this.drag = null;
    this.over = null;
  }
  lifted(x: Cell): boolean { return !!this.drag && this.drag.moved && this.drag.src === x.clip.src; }
  shift(x: Cell): string { return this.lifted(x) ? `translate(${this.drag.dx}px, ${this.drag.dy}px)` : null; }

  openCell(x: Cell): void {
    if (!this.dragJustEnded) this.open.emit(x.clip);
  }

  toggleFull(): void {
    this.full = !this.full;
    try {
      if (this.full && this.root.nativeElement.requestFullscreen) this.root.nativeElement.requestFullscreen().catch(() => {});
      if (!this.full && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    } catch (e) { /* filling the window is enough */ }
  }
  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    if (!document.fullscreenElement) this.full = false;
  }

  close(): void {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    this.closed.emit();
  }
  onBackdrop(e: MouseEvent): void {
    if (e.target === this.root.nativeElement) this.close();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (this.covered || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === ' ') { this.togglePlay(); e.preventDefault(); }
    else if (e.key === 'f' || e.key === 'F') { this.toggleFull(); e.preventDefault(); }
    else if (e.key === 'Escape') { if (this.full) this.toggleFull(); else this.close(); e.preventDefault(); }
  }

  ngOnDestroy(): void {
    clearTimeout(this.limitTimer);
    if (this.full && document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }
}
